'use client';

import { formatNumber } from '@lichtblick/localization';
import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { ChevronRightXs } from '@lichtblick/photon/icons/arrows/chevron-right-xs';
import { Co2OffM } from '@lichtblick/photon/icons/energy/co2-off-m';
import { PlantM } from '@lichtblick/photon/icons/objects/plant-m';
import { Stepper } from '@lichtblick/photon/molecules/stepper';
import { Textfield } from '@lichtblick/photon/molecules/textfield';
import { useTracker } from '@lichtblick/tracker';
import classNames from 'classnames';
import { type FC, type ReactElement, useEffect, useState } from 'react';
import { B2bSpCommodity } from '../../../types/generated.lichtblick';
import { useVpCodes, type VpCodes } from '../../OAllInOneCalculator/useVpCodes';
import styles from './all-in-one-calculator.module.css';
import { MAX_CONSUMPTION, MIN_CONSUMPTION, MIN_METERS } from './constants';

interface CommodityConfig {
  badge: ReactElement;
  consumptionField: string;
  consumptionLabel: string;
  product: string;
  url: string;
}

const CONFIG: Record<B2bSpCommodity, CommodityConfig> = {
  [B2bSpCommodity.Electricity]: {
    badge: (
      <div className={classNames(styles.badge, styles['badge-electricity'])}>
        <PlantM />
        <div>
          <Text bold>100% Ökostrom</Text>
          <Text size="s">aus erneuerbaren Quellen</Text>
        </div>
      </div>
    ),
    consumptionField: 'strom',
    consumptionLabel: 'Stromverbrauch',
    product: 'GewerbeStrom',
    url: '/checkout-b2b/gewerbestrom/',
  },
  [B2bSpCommodity.Gas]: {
    badge: (
      <div className={classNames(styles.badge, styles['badge-gas'])}>
        <Co2OffM />
        <div>
          <Text bold>CO₂-Emissionen reduzieren</Text>
          <Text size="s">durch Beimischung von BioGas</Text>
        </div>
      </div>
    ),
    consumptionField: 'gas',
    consumptionLabel: 'Gasverbrauch',
    product: 'GewerbeGas',
    url: '/checkout-b2b/gewerbegas/',
  },
};

interface CommoditySubcalculator {
  commodity: B2bSpCommodity;
}

export const CommoditySubcalculator: FC<CommoditySubcalculator> = ({ commodity }) => {
  const { computeVpCodes } = useVpCodes();
  const { trackCustomEvent } = useTracker();
  const [vpCodes, setVpCodes] = useState<VpCodes>({});
  const { badge, consumptionField, consumptionLabel, product, url } = CONFIG[commodity];

  useEffect(() => setVpCodes(computeVpCodes()), [computeVpCodes]);

  return (
    <form
      action={url}
      method="GET"
      data-testid={`${commodity}-form`}
      onSubmit={({ currentTarget }) => {
        if (currentTarget.checkValidity()) {
          const data = new FormData(currentTarget);

          trackCustomEvent({
            eventName: 'b2b_calculator_show_lead_form',
            customProps: {
              product,
              meter_counter: Number(data.get('zaehler')),
              consumption_calculator: `${data.get(consumptionField)?.toString()} kWh/Jahr`,
            },
          });
        }
      }}
    >
      <div className={styles['input-grid']}>
        <div className={styles['input-row-2']}>
          <Stepper label="Anzahl Zähler" min={MIN_METERS} name="zaehler" hideLabelSuffix />
          <Textfield
            inputMode="numeric"
            label={consumptionLabel}
            max={MAX_CONSUMPTION}
            min={MIN_CONSUMPTION}
            name={consumptionField}
            errorMessage={`Der Verbrauch muss zwischen ${formatNumber(MIN_CONSUMPTION)} und ${formatNumber(MAX_CONSUMPTION)} kWh/Jahr liegen.`}
            rightSlot={
              <Text className={styles.unit} size="s">
                kWh/Jahr
              </Text>
            }
            type="number"
            required
            hideLabelSuffix
          />
        </div>
      </div>
      {Boolean(vpCodes.vp) && <input name="vp" type="hidden" value={vpCodes.vp} />}
      {Boolean(vpCodes.uvp) && <input name="uvp" type="hidden" value={vpCodes.uvp} />}
      <div className={styles['confirmation-row']}>
        {badge}
        <BoxCTA
          as="button"
          rightSlot={<ChevronRightXs />}
          type="submit"
          data-dd-action-name={`Calculator submit: ${product}`}
        >
          Unverbindlich anfragen
        </BoxCTA>
      </div>
    </form>
  );
};
